
import { defineComponent, ref } from "vue";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ListAutoDispose } from "@amcharts/amcharts5/.internal/core/util/List";
import { getImageByFile } from "@/service/image.service";
import SamplingPointImageComponent from "./SamplingPointImage.component.vue";
import { Image } from "@/types/Image.type";
import { ElMessage } from "element-plus";
import { Color } from "@amcharts/amcharts5/.internal/core/util/Color";
import am5lang_es_ES from "@amcharts/amcharts5/locales/es_ES";
import { i18n } from "@/plugins/i18n.plugin";
import securityService from "@/service/security.service";
import { AppRole } from "@/types/app.type";
import { createRange } from "@/utils/chart.util";
import { Filter } from "@/types/filter.type";
import { getTmoa } from "@/service/tmoa.service";

interface DataChart {
  dateTime: number;
  value: number;
  name: string;
  unit?: string;
  fileName?: string;
  bulletSettings?: { fill: Color };
  strokeSettings?: { stroke: Color };
}

interface DataContext {
  value: number;
}

const AVERAGE_COLOR = am5.color(0x0a0a0a);
export default defineComponent({
  components: {
    SamplingPointImageComponent,
  },
  setup() {
    const chart = "" as unknown as am5xy.XYChart;
    // Leyenda
    const legend = "" as unknown as am5.Legend;
    // Id grafica
    const chartId = "chartdiv";
    // Datos de las estimaciones para graficar
    const series = "" as unknown as am5xy.LineSeries;
    // Datos de las tmoas para graficar
    const seriesTmoa = "" as unknown as am5xy.LineSeries;
    // Datos de las estimaciones para los scrolls
    const sbseries = "" as unknown as am5xy.LineSeries;
    // Visibilidad del modal de imagen
    const visible = ref(false);

    const hasData = ref(true);
    const isAppDemo = securityService.hasRole(AppRole.DEMOS) ? true : false;

    const yAxisRef = "" as unknown as am5xy.ValueAxis<am5xy.AxisRenderer>;

    // const showTmoa = ref(false);

    return {
      chart,
      legend,
      chartId,
      series,
      seriesTmoa,
      sbseries,
      visible,
      hasData,
      isAppDemo,
      yAxisRef,
      // showTmoa,
    };
  },
  data() {
    return {
      // Fechas y horas dataPicker
      startDatePicker: "" as unknown as Date | undefined,
      endDatePicker: "" as unknown as Date | undefined,

      lastImage: undefined as Image | undefined,
      fileNames: [] as unknown as string[],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      estData: undefined as any,
      currentIndex: undefined as number | undefined,
      currentIndexTmoa: undefined as number | undefined,
      isParameter: undefined as boolean | undefined,
      maxValue: undefined as number | undefined,
      minValue: undefined as number | undefined,
      averageValue: undefined as number | undefined,
      yAxisTmoa: undefined as am5xy.ValueAxis<am5xy.AxisRendererY> | undefined,
      max: 6.5,
      meanLine: null as am5.DataItem<am5xy.IValueAxisDataItem> | null,
      // Valores para la media
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rangeDataItem: undefined as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      range: undefined as any,

      showTmoa: false as boolean,
    };
  },
  props: {
    samplingPoint: { type: Object, required: false, immediate: true, deep: true },
    // parameter: { type: Object, required: false, deep: true },
    parameter: { type: Object, required: false, immediate: true, deep: true },
    estimation: {
      type: Object as () => DataChart[],
      required: false,
    },
    tmoa: {
      type: Object as () => DataChart[],
      required: false,
      deep: true,
    },
    alert: {
      type: Object as () => DataChart[],
      required: false,
      deep: true,
    },
    startDate: { type: Date, required: false, deep: true },
    endDate: { type: Date, required: false, deep: true },
  },
  mounted() {
    this.$nextTick(async () => {
      this.updateChartData();
      window.addEventListener("keydown", this.handleKey);
    });
  },
  methods: {
    handleKey(event: { key: string }) {
      if (event.key === "ArrowRight") {
        this.nextImg();
      }
      if (event.key === "ArrowLeft") {
        this.prevImg();
      }
    },
    updateChartData() {
      if (this.estimation && this.estimation.length > 0) {
        this.hasData = true;
        this.fillData(this.estimation);
      } else {
        this.hasData = false;
      }
    },
    addScrollbar(chart: am5xy.XYChart, root: am5.Root, estimation: DataChart[]) {
      var scrollbarX = chart.set(
        "scrollbarX",
        am5xy.XYChartScrollbar.new(root, {
          orientation: "horizontal",
          height: 30,
        })
      );

      var scrollbarY = chart.set(
        "scrollbarY",
        am5.Scrollbar.new(root, { orientation: "vertical", width: 25 })
      );

      scrollbarY?.get("background")?.setAll({
        strokeOpacity: 0,
      });
      var sbxAxis = scrollbarX.chart.xAxes.push(
        am5xy.DateAxis.new(root, {
          groupData: false,
          visible: false,
          baseInterval: { timeUnit: "millisecond", count: 1 },
          renderer: am5xy.AxisRendererX.new(root, {
            minorGridEnabled: false,
          }),
        })
      );

      var yRenderer = am5xy.AxisRendererY.new(root, {});
      yRenderer.grid.template?.set("forceHidden", false);

      var sbyAxis = scrollbarX.chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yRenderer,
        })
      );

      this.sbseries = scrollbarX.chart.series.push(
        am5xy.LineSeries.new(root, {
          xAxis: sbxAxis,
          yAxis: sbyAxis,
          valueYField: "value",
          valueXField: "dateTime",
        })
      );
      this.sbseries.fills.template?.setAll({
        fillOpacity: 0.6,
        visible: true,
        strokeOpacity: 0,
      });

      this.sbseries.data.setAll(estimation);
    },

    /**
     * Generar gráfica
     * @param estimation
     */
    async fillData(estimation: DataChart[]) {
      let root = am5.Root.new("chartdiv");
      root.locale = am5lang_es_ES;
      // Set themes
      root.setThemes([am5themes_Animated.new(root)]);

      // Create chart
      this.chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          focusable: true,
          panX: true,
          panY: true,
          wheelX: "panX",
          wheelY: "zoomX",
          paddingLeft: 0,
        })
      );

      let cursor = this.chart.set("cursor", am5xy.XYCursor.new(root, {}));
      cursor.lineX.set("visible", true);
      cursor.lineY.set("visible", false);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      var easing = am5.ease.linear;
      this.chart.get("colors")?.set("step", 2);

      // Create axes
      const xAxis = this.chart.xAxes.push(
        am5xy.DateAxis.new(root, {
          maxDeviation: 0,
          groupData: false,
          baseInterval: {
            timeUnit: "minute",
            count: 1,
          },
          renderer: am5xy.AxisRendererX.new(root, {
            minorGridEnabled: true,
          }),
        })
      );
      //- Crear eje estimaciones
      this.createAxisYEstimation(root, xAxis, estimation);

      // Crear eje si hay TMOA
      this.createAxisYTmoa(root, xAxis);

      if (this.chart && estimation) {
        this.addScrollbar(this.chart, root, estimation);
      }

      this.createLegend(root);

      this.series.appear(1000);
      this.chart.appear(1000, 100);
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getImage(ev: any, type: string) {
      if (type == "estimation") {
        this.isParameter = true;
      }

      try {
        const dataContext = ev.target.dataItem?.dataContext as DataChart;

        let filteredAlerts;
        // filteredAlerts = this.alert?.filter((item) => item.value === 1) ?? [];
        if (this.tmoa) {
          filteredAlerts = (this.tmoa?.filter((item) => item.fileName !== undefined) ?? []).sort(
            (a, b) => a.dateTime - b.dateTime
          );
        } else if (this.alert) {
          filteredAlerts = (this.alert?.filter((item) => item.fileName !== undefined) ?? []).sort(
            (a, b) => a.dateTime - b.dateTime
          );
        } else {
          console.log("[getImage] no hay alerts para filtrar");
        }

        this.fileNames = (
          type === "estimation" ? this.estimation ?? [] : filteredAlerts ?? this.tmoa ?? []
        )
          .map((item: DataChart) => item.fileName)
          .filter((fileName): fileName is string => fileName !== undefined);

        const index = this.fileNames.findIndex((img) => img === dataContext.fileName);

        if (index !== -1) {
          if (type === "estimation") {
            this.currentIndex = index;
          } else {
            this.currentIndexTmoa = index;
          }

          getImageByFile(this.fileNames[index])
            .then((image) => {
              this.lastImage = image;
              this.visible = true;
            })
            .catch((error) => {
              ElMessage.error("Error al obtener la imagen por archivo.");
            });
        } else {
          ElMessage.warning("No se encontró la imagen correspondiente en la lista.");
        }
      } catch (error) {
        ElMessage.error("Ocurrió un error al intentar cargar las imágenes.");
      }
    },
    /**
     * Crear leyenda de la gráfica
     * @param root
     */
    createLegend(root: am5.Root) {
      // Limpiar leyenda anterior si existe
      if (root.container.children.length > 1) {
        root.container.children.pop();
      }

      // Crear contenedor horizontal, cnetrado
      let legendContainer = am5.Container.new(root, {
        layout: root.horizontalLayout,
        x: am5.percent(50),
        centerX: am5.percent(50),
        y: am5.percent(101),
        centerY: am5.percent(101),
        paddingTop: 20,
        paddingLeft: 20,
        layer: 100,
      });

      root.container.children.push(legendContainer);

      const titleLine = this.createLegendItemLine(root, "", am5.color(0x87cefa));
      titleLine.setAll({ centerY: am5.percent(50), marginRight: 0 });
      legendContainer.children.push(titleLine);

      // Nombre del parámetro
      const paramName = this.parameter?.name ?? "";
      const unit = this.parameter?.unit ?? "-";

      const titleLabel = am5.Label.new(root, {
        text: paramName,
        fontWeight: "normal",
        fontSize: 14,
        fill: am5.color(0x000000),
        centerY: am5.percent(50),
      });
      legendContainer.children.push(titleLabel);

      // Si es TMOA  solo mostrar anomalía
      if (this.parameter?.code === "TMOA") {
        const anomalyItem = this.createLegendItemDot(root, "Anomalía", am5.color(0xf37878));
        anomalyItem.setAll({ centerY: am5.percent(50), marginLeft: 20 });
        legendContainer.children.push(anomalyItem);
        return;
      }

      if (
        this.parameter?.code !== "TMOA" &&
        this.samplingPoint?.numTmoa > 0 &&
        this.samplingPoint?.parameters.some((p: { code: string }) => p.code === "TMOA")
      ) {
        const tmoaItem = this.createLegendItemLine(root, "Matriz de Agua", am5.color(0x7986cb));
        tmoaItem.setAll({ centerY: am5.percent(50), marginLeft: 20 });
        legendContainer.children.push(tmoaItem);
      }

      // Si es otro parámetro  mostrar min, max, media
      const minItem = this.createLegendItemDot(
        root,
        `Mínimo: ${
          this.minValue != null ? i18n.global.n(this.minValue, "fixed") + " " + unit : "-"
        }`,
        am5.color(0x50c878)
      );
      minItem.setAll({ centerY: am5.percent(50), marginLeft: 15 });
      legendContainer.children.push(minItem);

      const maxItem = this.createLegendItemDot(
        root,
        `Máximo: ${
          this.maxValue != null ? i18n.global.n(this.maxValue, "fixed") + " " + unit : "-"
        }`,
        am5.color(0xffa500)
      );
      maxItem.setAll({ centerY: am5.percent(50), marginLeft: 15 });
      legendContainer.children.push(maxItem);

      const avgItem = this.createLegendItemLine(
        root,
        `Media: ${
          this.averageValue != null ? i18n.global.n(this.averageValue, "fixed") + " " + unit : "-"
        }`,
        am5.color(0xc8c8c8)
      );
      avgItem.setAll({ centerY: am5.percent(50), marginLeft: 15 });
      legendContainer.children.push(avgItem);
    },
    createLegendItemDot(root: am5.Root, name: string, color: am5.Color) {
      let legendItem = am5.Container.new(root, {
        layout: root.horizontalLayout,
        marginBottom: 0,
        centerY: am5.percent(50),
      });

      let colorDot = am5.Circle.new(root, {
        radius: 5,
        fill: color,
        marginRight: 0,
        marginLeft: 15,
        centerY: am5.percent(50),
      });

      let label = am5.Label.new(root, {
        text: name,
        fontSize: 14,
        marginRight: 10,
        centerY: am5.percent(50),
      });

      legendItem.children.push(colorDot);
      legendItem.children.push(label);
      return legendItem;
    },
    createLegendItemLine(root: am5.Root, name: string, color: am5.Color) {
      let legendItem = am5.Container.new(root, {
        layout: root.horizontalLayout,
        marginBottom: 0,
        centerY: am5.percent(50),
      });

      if (!name) {
        // Línea decorativa doble de larga si no hay texto, para parametro
        let longLine = am5.Rectangle.new(root, {
          width: 20,
          height: 2,
          fill: color,
          centerY: am5.percent(50),
        });
        legendItem.children.push(longLine);
      } else if (name == "Matriz de Agua") {
        let longLine = am5.Rectangle.new(root, {
          width: 20,
          height: 2,
          fill: color,
          centerY: am5.percent(50),
        });
        legendItem.children.push(longLine);

        let label = am5.Label.new(root, {
          text: name,
          fontSize: 14,
          marginRight: 5,
          centerY: am5.percent(50),
          interactive: true,
          cursorOverStyle: "pointer",
          background: am5.Rectangle.new(root, {
            fillOpacity: 0,
            fill: color,
          }),
        });
        label.set("fill", this.showTmoa ? am5.color(0x000000) : am5.color(0xaaaaaa));

        label.events.on("pointerdown", () => {
          this.showTmoa = !this.showTmoa;
          label.set("fill", this.showTmoa ? am5.color(0x000000) : am5.color(0xaaaaaa));
        });

        legendItem.children.push(label);
      } else {
        //  2 líneas cortas + texto, para la media
        let colorLine1 = am5.Rectangle.new(root, {
          width: 10,
          height: 2,
          fill: color,
          marginRight: 3,
          centerY: am5.percent(50),
        });

        let colorLine2 = am5.Rectangle.new(root, {
          width: 10,
          height: 2,
          fill: color,
          marginRight: 3,
          centerY: am5.percent(50),
        });

        let label = am5.Label.new(root, {
          text: name,
          fontSize: 14,
          marginRight: 5,
          centerY: am5.percent(50),
        });

        legendItem.children.push(colorLine1);
        legendItem.children.push(colorLine2);
        legendItem.children.push(label);
      }

      return legendItem;
    },
    /**
     *
     * @param root
     * @param xAxis
     * @param estimation
     */
    async createAxisYEstimation(
      root: am5.Root,
      xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
      estimation: DataChart[]
    ) {
      // -- export
      var yRenderer_1 = am5xy.AxisRendererY.new(root, {
        opposite: false,
      });
      var yAxis = this.chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yRenderer_1,
        })
      );

      this.yAxisRef = yAxis;

      //- Media
      const { rangeDataItem, range } = createRange(
        yAxis,
        this.averageValue ?? 0,
        undefined,
        AVERAGE_COLOR,
        true
      );

      this.rangeDataItem = rangeDataItem;
      this.range = range;

      if (this.chart.yAxes.indexOf(yAxis) > 0) {
        yAxis.set(
          "syncWithAxis",
          (this.chart.yAxes as ListAutoDispose<am5xy.ValueAxis<am5xy.AxisRenderer>>).getIndex(0)
        );
      }

      this.series = this.chart.series.push(
        am5xy.LineSeries.new(root, {
          name: `${this.parameter?.name ? this.parameter.name : "Sin Datos"}`,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "value",
          valueXField: "dateTime",
        })
      );

      yRenderer_1.grid.template.set("strokeOpacity", 0.05);
      yRenderer_1.labels.template.set("fill", this.series.get("fill"));
      // Eje y
      yRenderer_1.setAll({
        stroke: this.series.get("fill"), //Color del relleno
        strokeOpacity: 1,
        opacity: 1,
      });

      //Obtener TMOAs para mostrar nombre en el tooltip
      const filter: Filter = {
        fields: {
          "tmoa.samplingPointId": {
            operator: "equal",
            value: this.samplingPoint?.id | 0,
          },
        },
      };

      const tmoas = await getTmoa(filter);

      this.series.bullets.push((root, _, dataItem) => {
        // Obtener el valor actual del bullet
        let dataContext = dataItem.dataContext as DataContext;
        let value = dataContext.value;
        let opacity = this.parameter?.code === "TMOA" ? 1 : 0;
        let circleColor = this.series.get("fill");
        let radius = 3;
        if (
          value === this.minValue &&
          this.parameter?.code !== "ALT" &&
          this.parameter?.code !== "TMOA"
        ) {
          circleColor = am5.color(0x50c878);
          opacity = 1;
        } else if (
          value === this.maxValue &&
          !["ALT", "TMOA"].includes(this.parameter?.code ?? "")
        ) {
          circleColor = am5.color(0xffa500);
          opacity = 1;
        } else if (value === this.maxValue && value !== 0 && this.parameter?.code === "ALT") {
          circleColor = am5.color(0xf37878);
          opacity = 1;
        } else if (
          value === this.samplingPoint?.numTmoa &&
          value !== 0 &&
          this.parameter?.code === "TMOA"
        ) {
          circleColor = am5.color(0xf37878);
          opacity = 1;
        }
        // console.log("AAA - this.parameter?.code", this.parameter?.code);
        let bulletCircle = am5.Circle.new(root, {
          radius: radius,
          fill: circleColor,
          opacity: opacity,
          cursorOverStyle: "pointer",
          tooltipHTML:
            this.parameter?.code === "TMOA"
              ? `
            <div style="display: flex; flex-direction: column; align-items: center; text-align: center;">
              <div>{valueX.formatDate('dd/MM/yyyy HH:mm')}</div>
              <div style="font-weight: bold;">Valor: {valueY.formatNumber('#,###.##')}</div>
              <div style="font-weight: bold;">{name}</div>
              </div>
              `
              : `
            <div style="display: flex; justify-content: center; align-items: center; height: 100%; text-align: center;">
                <div>
                    <div>{valueX.formatDate('dd/MM/yyyy HH:mm')}</div>
                    <div style="font-weight: bold;">Valor: {valueY.formatNumber('#,###.##')}</div>
                </div>
            </div>
          `,
        });

        // Crear un círculo invisible para el evento click
        let invisibleCircle = am5.Circle.new(root, {
          radius: 5,
          fill: circleColor,
          opacity: 0,
          cursorOverStyle: "pointer",
        });

        invisibleCircle.events.on("click", (event: any) => {
          this.getImage(event, "estimation");
        });

        let container: any = am5.Container.new(root, {});
        container.children.push(invisibleCircle);
        container.children.push(bulletCircle);
        container.events.on("click", (event: any) => {
          this.getImage(event, "estimation");
        });

        return am5.Bullet.new(root, {
          sprite: container,
        });
      });

      this.series.fills.template.setAll({ fillOpacity: 0.3, visible: true });

      //Añadir .name de TMOA a los datos graficados
      const tmoaMap = new Map<number, string>();
      tmoas.forEach((t) => {
        tmoaMap.set(t.value ? t.value : 0, t.name || `${t.value}`);
      });

      const enrichedTmoaData = estimation.map((d) => {
        const name = tmoaMap.get(d.value) ?? `${d.value}`;
        return {
          ...d,
          name,
        };
      });

      this.series.data.setAll(enrichedTmoaData);

      // this.series.data.setAll(estimation);
      return yAxis;
    },

    // Crear ejeY secundario solo para Matriz de agua
    async createAxisYTmoa(root: am5.Root, xAxis: am5xy.DateAxis<am5xy.AxisRenderer>) {
      // Crear eje Y secundario (TMOA)
      let yRenderer = am5xy.AxisRendererY.new(root, {
        opposite: true,
      });
      let yAxis_2 = this.chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          max: this.samplingPoint?.numTmoa + 0.5,
          maxPrecision: 0,
          strictMinMax: true,
          renderer: yRenderer,
        })
      );

      if (this.chart.yAxes.indexOf(yAxis_2) > 0) {
        yAxis_2.set(
          "syncWithAxis",
          (this.chart.yAxes as ListAutoDispose<am5xy.ValueAxis<am5xy.AxisRenderer>>)?.getIndex(0)
        );
      }

      // Crear serie TMOA
      this.seriesTmoa = this.chart.series.push(
        am5xy.LineSeries.new(root, {
          name: "Estado",
          xAxis: xAxis,
          yAxis: yAxis_2,
          valueYField: "value",
          valueXField: "dateTime",
        })
      );

      this.seriesTmoa.strokes.template.setAll({ strokeWidth: 1 });
      this.seriesTmoa.strokes.template.set("templateField", "strokeSettings");

      yRenderer.grid.template.set("strokeOpacity", 0.05);
      yRenderer.labels.template.set("fill", this.seriesTmoa.get("fill"));
      yRenderer.setAll({
        stroke: this.seriesTmoa.get("fill"),
        strokeOpacity: 1,
        opacity: 1,
      });

      this.seriesTmoa.set("fill", am5.color("#00ff00"));

      //Obtener TMOAs para mostrar nombre en el tooltip
      const filter: Filter = {
        fields: {
          "tmoa.samplingPointId": {
            operator: "equal",
            value: this.samplingPoint?.id | 0,
          },
        },
      };

      const tmoas = await getTmoa(filter);

      // Tooltip y bullets
      this.seriesTmoa.bullets.push(() => {
        let circle = am5.Circle.new(root, {
          radius: 3,
          templateField: "bulletSettings",
          strokeWidth: 1,
          cursorOverStyle: "pointer",
          // tooltipText: "{valueX.formatDate('dd/MM/yyyy HH:mm:ss')} \n[bold]Valor: [/] {valueY}",
          tooltipHTML: `
          <div style="display: flex; flex-direction: column; align-items: center; text-align: center;">
            <div>{valueX.formatDate('dd/MM/yyyy HH:mm:ss')}</div>
            <div style="font-weight: bold;">Valor: {name}</div>
          </div>
        `,
        });

        let invisibleCircle = am5.Circle.new(root, {
          radius: 8,
          fill: am5.color(0x000000),
          opacity: 0,
          cursorOverStyle: "pointer",
        });

        invisibleCircle.events.on("click", (event: any) => {
          this.getImage(event, "tmoa");
        });
        circle.events.on("click", (event: any) => {
          this.getImage(event, "tmoa");
        });

        return am5.Bullet.new(root, {
          sprite: circle,
        });
      });

      //Añadir .name de TMOA a los datos graficados
      const tmoaMap = new Map<number, string>();
      tmoas.forEach((t) => {
        tmoaMap.set(t.value ? t.value : 0, t.name || `${t.value}`);
      });

      const enrichedTmoaData = (this.tmoa as DataChart[]).map((d) => ({
        ...d,
        name: tmoaMap.get(d.value) ?? `${d.value}`,
      }));

      // this.seriesTmoa.data.setAll(this.tmoa as DataChart[]);
      this.seriesTmoa.data.setAll(enrichedTmoaData);

      this.yAxisTmoa = yAxis_2 as am5xy.ValueAxis<am5xy.AxisRendererY>;

      //Si el parametro seleccionado es Matriz de agua, NO grafica otra vez
      const isTMOA = this.parameter?.code === "ALT" || this.parameter?.code === "TMOA";
      if (
        isTMOA ||
        !this.samplingPoint?.parameters.some((p: { code: string }) => p.code === "TMOA")
      ) {
        this.handleShowTmoaAndAxis(false, this.yAxisTmoa);
        return;
      }
      this.handleShowTmoaAndAxis(true, this.yAxisTmoa);
      return yAxis_2;
    },
    seeImages() {
      this.visible = true;
    },
    async nextImg() {
      let currentIndex, fileNames;

      if (this.isParameter) {
        currentIndex = this.currentIndex;
        fileNames = this.fileNames;
      } else {
        currentIndex = this.currentIndexTmoa;
        fileNames = this.fileNames;
      }
      if (currentIndex !== undefined && currentIndex < fileNames.length - 1) {
        currentIndex++;

        try {
          this.lastImage = await getImageByFile(fileNames[currentIndex]);

          if (this.isParameter) {
            this.currentIndex = currentIndex;
          } else {
            this.currentIndexTmoa = currentIndex;
          }
        } catch (error) {
          ElMessage.error("Ocurrió un error al cargar la siguiente imagen.");
        }
      } else {
        ElMessage.warning("Esta es la última imagen.");
      }
    },

    async prevImg() {
      let currentIndex, fileNames;

      if (this.isParameter) {
        currentIndex = this.currentIndex;
        fileNames = this.fileNames;
      } else {
        currentIndex = this.currentIndexTmoa;
        fileNames = this.fileNames;
      }
      if (currentIndex !== undefined && currentIndex > 0) {
        currentIndex--;

        try {
          this.lastImage = await getImageByFile(fileNames[currentIndex]);

          if (this.isParameter) {
            this.currentIndex = currentIndex;
          } else {
            this.currentIndexTmoa = currentIndex;
          }
        } catch (error) {
          ElMessage.error("Ocurrió un error al cargar la imagen anterior.");
        }
      } else {
        ElMessage.warning("Esta es la primera imagen.");
      }
    },

    changeOpacityRange(value: number) {
      this.range.get("grid").setAll({
        strokeOpacity: value,
      });
    },
    handleShowTmoaAndAxis(show: boolean, axis: any) {
      if (!axis || this.parameter?.code === "TMOA") {
        return;
      }

      if (show && this.tmoa && this.tmoa.length > 0) {
        this.seriesTmoa.data.setAll(this.tmoa);
        axis.set("visible", true);
        axis.set("maxPrecision", 0);
        axis.set("max", this.samplingPoint?.numTmoa + 0.5);
      } else {
        this.seriesTmoa.data.setAll([]);
        axis.set("visible", false);
      }

      if (this.legend) {
        this.legend.data?.setAll(this.chart.series.values);
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKey);
  },
  watch: {
    estimation: async function () {
      if (this.estimation && this.estimation.length > 0) {
        // Calcular la media
        const filteredValues = this.estimation
          .map((item) => item.value)
          .filter((value) => value !== null && value !== undefined);

        this.averageValue =
          filteredValues.length > 0
            ? filteredValues.reduce((sum, value) => sum + value, 0) / filteredValues.length
            : undefined;

        this.minValue = Math.min(
          ...this.estimation
            .map((item) => item.value)
            .filter((value) => value !== null && value !== undefined)
        );
        this.maxValue = Math.max(
          ...this.estimation
            .map((item) => item.value)
            .filter((value) => value !== null && value !== undefined)
        );
        if (!this.sbseries) {
          this.fillData(this.estimation);
        } else {
          // Cuando se cambia de parámetro o de fecha
          this.series.data?.setAll(this.estimation);
          this.sbseries.data?.setAll(this.estimation);

          //- Cambiar valores Media
          if (this.rangeDataItem && this.averageValue !== undefined) {
            this.rangeDataItem.set("value", this.averageValue ?? 0);
            this.rangeDataItem.set("endValue ", undefined);
            if (this.parameter?.code == "TMOA") {
              if (this.yAxisRef) {
                this.yAxisRef.set("min", 0);
                this.yAxisRef.set(
                  "max",
                  this.samplingPoint?.numTmoa !== 0 ? this.samplingPoint?.numTmoa + 0.5 : undefined
                );
                this.yAxisRef.set("maxPrecision", 0);
                this.yAxisRef.set("strictMinMax", this.samplingPoint?.numTmoa !== 0 ? true : false);
              }
              this.changeOpacityRange(0);
            } else {
              this.yAxisRef.set("min", undefined);
              this.yAxisRef.set("max", undefined);
              this.yAxisRef.set("maxPrecision", undefined);
              this.yAxisRef.set("strictMinMax", false);
              this.changeOpacityRange(0.2);
            }
          }

          this.chart.root;
          if (this.chart.root.container.children.length > 1) {
            this.chart.root.container.children.pop();
          }
          if (this.legend) {
            this.chart.children.removeValue(this.legend);
          }
          this.createLegend(this.chart.root);
          if (!this.chart.yAxes.getIndex(0)?.get("visible")) {
            this.chart.yAxes.getIndex(0)?.set("visible", true);
          }
          if (this.parameter?.code === "ALT") {
            const renderer = this.chart.yAxes.getIndex(1)?.get("renderer") as am5xy.AxisRendererY;
            renderer.set("opposite", true);
          }
          this.series.set("name", `${this.parameter?.name}`);
        }
      } else {
        this.minValue = undefined;
        this.maxValue = undefined;
        this.averageValue = undefined;
        if (this.sbseries && this.series) {
          this.series.data.setAll([] as DataChart[]);
          this.sbseries.data.setAll([] as DataChart[]);
          this.series.set("name", `${this.parameter?.name}`);

          if (this.legend) {
            this.legend.data?.setAll([] as DataChart[]);
          }
          //- Ocultar media
          this.changeOpacityRange(0);

          ElMessage.warning(this.$t("No hay datos en el periodo seleccionado"));
        } else {
          let root = this.chart.root;
          if (root?.container && root.container.children.length > 1) {
            root.container.children.pop();
          }
          this.fillData([]);
        }
      }
    },
    tmoa: async function () {
      //Si el parametro es TMOA o ALT no grafica en el eje Y secundario
      // if (this.parameter?.code === "TMOA" && this.parameter?.code === "ALT") {
      if (["TMOA", "ALT"].includes(this.parameter?.code)) {
        if (this.seriesTmoa) {
          this.seriesTmoa.data.setAll([]);
        }
        if (this.yAxisTmoa) {
          this.yAxisTmoa.set("visible", false);
        }
        return;
      }

      //Grafica el TMOA si hay en el eje Y secundario
      if (
        this.tmoa &&
        this.tmoa.length > 0 &&
        this.seriesTmoa &&
        this.parameter?.code !== "TMOA" &&
        this.showTmoa
      ) {
        this.seriesTmoa.data.setAll(this.tmoa);
        if (this.yAxisTmoa) {
          this.yAxisTmoa.set("visible", true);
          this.yAxisTmoa.set("maxPrecision", 0);
          this.yAxisTmoa.set("max", this.samplingPoint?.numTmoa + 0.5);
        }
        if (this.legend) {
          this.legend.data?.setAll([]);
          this.legend.data?.setAll(this.chart.series.values);
        }
      } else {
        if (this.seriesTmoa) {
          this.seriesTmoa.data.setAll([] as DataChart[]);
        }
        if (this.yAxisTmoa) {
          this.yAxisTmoa.set("visible", false);
        }
        if (this.legend) {
          this.legend.data?.setAll([] as DataChart[]);
          this.legend.data?.setAll(this.chart.series.values);
        }
      }
    },
    startDatePicker: async function () {
      this.updateChartData();
    },
    endDatePicker: async function () {
      this.updateChartData();
    },
    showTmoa(newVal: boolean) {
      this.handleShowTmoaAndAxis(newVal, this.yAxisTmoa);
    },
  },
});
